import { getCountries, getCountryCallingCode, parsePhoneNumberFromString, type CountryCode, type PhoneNumber } from "libphonenumber-js/max";
import { COUNTRIES } from "./countries.js";
/**
 * Phone numbers (E.164). Parsing / validation / formatting use libphonenumber-js (MIT, Google's libphonenumber
 * metadata, "max" set = full validation per country). The default country comes from the branch / tenant country
 * (ISO 3166 alpha-2): for MY, "012-345 8821" becomes "+60123458821"; numbers typed with "+" or "00" are parsed as
 * international and their country is detected.
 *
 * (Phone input task, 2026-10-11) normalizePhone is STRICT: a national number that isn't valid for the default
 * country returns null instead of silently getting the default calling code prefixed (Kozor SG typed the Malaysian
 * 0164366293 → it used to become +65164366293). phoneHint() then suggests the likely country.
 *
 * (Phone loosen task, 2026-10-11) Strict isValid() blocked real customers (metadata lags operator allocations: MY
 * 11-digit 014/016/010, 015 VoIP, Sarawak/Sabah landlines …). Now a number is ACCEPTED when it is *possible* for its
 * country (libphonenumber isPossible = length check, national input must keep the selected calling code) and not
 * garbage; Malaysia uses an explicit rule (never metadata): after the trunk 0, "1…" = mobile with 9–10 digits,
 * "3…"–"9…" = landline with 8–9 digits. Possible-but-not-strictly-valid numbers are saved; the UI shows a soft
 * "请确认号码是否正确" note (PhoneInfo.confirm). The did-you-mean hint needs strict-invalid here + strictly valid elsewhere.
 *
 * (User decision 2026-10-11, overrides the above) Phone format NEVER blocks saving. Anything that isn't garbage
 * (isPhoneGarbage: < 5 digits / letters only / one repeated digit) is stored as best-effort E.164 (phoneBestEffort);
 * possible()/strict only decide the warning 「号码看起来不正确，请确认」 (+ did-you-mean 改用 where it applies).
 */

/** Calling codes for every ISO country (libphonenumber metadata, plus countries.ts for the few it lacks). */
export const COUNTRY_CALLING_CODES: Record<string, string> = {
  ...Object.fromEntries(Object.values(COUNTRIES).map((c) => [c.code, c.callingCode])),
  ...Object.fromEntries(getCountries().map((c) => [c, String(getCountryCallingCode(c))])),
};

const supported = new Set<string>(getCountries());
const cc = (country: string | null | undefined): CountryCode | undefined => {
  const c = (country ?? "").trim().toUpperCase();
  return supported.has(c) ? (c as CountryCode) : undefined;
};
/** "+65 9123 4567" / "0065…" → "+659123…"; separators dropped. */
const clean = (input: string) => {
  let s = String(input ?? "").trim().replace(/[\s\-().\u00a0/]/g, "");
  if (s.startsWith("00")) s = "+" + s.slice(2);
  return s;
};
const parse = (input: string, country?: string | null): PhoneNumber | undefined => {
  const s = clean(input);
  if (!s || !/^\+?\d+$/.test(s)) return undefined;
  try { return parsePhoneNumberFromString(s, s.startsWith("+") ? undefined : cc(country)) ?? undefined; } catch { return undefined; }
};

const MY_MOBILE = /^1\d{8,9}$/, MY_FIXED = /^[3-9]\d{7,8}$/;
/**
 * Acceptable number for its calling code: Malaysia by the explicit rule above, else libphonenumber isPossible();
 * one digit repeated (0000000000, 011-1111 1111) is garbage.
 */
function possible(p: PhoneNumber | undefined): p is PhoneNumber {
  if (!p) return false;
  const nn = String(p.nationalNumber);
  if (!/^\d+$/.test(nn) || /^(\d)\1+$/.test(nn)) return false;
  if (String(p.countryCallingCode) === "60") return MY_MOBILE.test(nn) || MY_FIXED.test(nn);
  return p.isPossible();
}
/**
 * National format; Malaysian numbers outside the metadata get the usual grouping (libphonenumber would print
 * "1-667-44-6215"): mobile 016-674 4621 / 016-6744 6215, 03-1234 5678, 082-123 456, 09-123 4567.
 */
function natFormat(p: PhoneNumber): string {
  const nn = String(p.nationalNumber);
  if (String(p.countryCallingCode) !== "60" || p.isValid()) return p.formatNational();
  if (MY_MOBILE.test(nn)) return "0" + nn.slice(0, 2) + "-" + nn.slice(2, nn.length - 4) + " " + nn.slice(-4);
  if (/^8\d{7}$/.test(nn)) return "0" + nn.slice(0, 2) + "-" + nn.slice(2, 5) + " " + nn.slice(5);
  if (MY_FIXED.test(nn)) return "0" + nn.slice(0, 1) + "-" + nn.slice(1, nn.length - 4) + " " + nn.slice(-4);
  return p.formatNational();
}
const intlFormat = (p: PhoneNumber) => String(p.countryCallingCode) === "60" && !p.isValid() ? displayIntl(p) : p.formatInternational();

/** Resolve the input for `country`: the number when acceptable (+ whether it is strictly valid), else null. */
function resolve(input: string, country: string): { p: PhoneNumber; strict: boolean } | null {
  const s = clean(input), code = COUNTRY_CALLING_CODES[(country ?? "").trim().toUpperCase()];
  const p = parse(input, country);
  // a national number must stay in the selected country's calling code (SG "016…" is parsed as IDD 016 + Austria → no)
  const ok = possible(p) && (s.startsWith("+") || (!!code && String(p.countryCallingCode) === code));
  // "60123458821" typed without "+" for a MY shop: the national number already carries the calling code
  if (code && /^\d+$/.test(s) && s.startsWith(code) && !s.startsWith("0") && !(ok && p!.isValid())) {
    const q = parse("+" + s);
    if (possible(q) && String(q.countryCallingCode) === code && (!ok || q.isValid())) return { p: q, strict: q.isValid() };
  }
  return ok ? { p: p!, strict: p!.isValid() } : null;
}

/**
 * For saving: an acceptable (possible, see above) number → E.164; anything else (incl. a national number impossible
 * for `country`) → null — the default calling code is never prefixed to a number that can't be one of that country.
 */
export function normalizePhone(input: string, country: string = "MY"): string | null {
  return resolve(input, country)?.p.number ?? null;
}
/** Strictly valid (libphonenumber isValid, full metadata) → E.164, else null. Used for the did-you-mean hint. */
export function normalizePhoneStrict(input: string, country: string = "MY"): string | null {
  const r = resolve(input, country);
  return r?.strict ? r.p.number : null;
}

/**
 * The old lenient rule (default calling code + national digits, no per-country check). Only for LOOKUPS of stored
 * numbers (search, referrer lookup) so rows saved before strict validation can still be found — never for writes.
 */
export function normalizePhoneLoose(input: string, country: string = "MY"): string | null {
  const strict = normalizePhone(input, country);
  if (strict) return strict;
  let digits = clean(input);
  if (digits.startsWith("+")) return /^\+[1-9]\d{6,14}$/.test(digits) ? digits : null;
  if (!/^\d+$/.test(digits)) return null;
  const code = COUNTRY_CALLING_CODES[(country ?? "").toUpperCase()];
  if (!code) return null;
  if (digits.startsWith(code) && !digits.startsWith("0") && digits.length >= code.length + 8) return "+" + digits;
  if (digits.startsWith("0") && country.toUpperCase() !== "IT") digits = digits.slice(1);
  const e164 = "+" + code + digits;
  return /^\+[1-9]\d{7,14}$/.test(e164) ? e164 : null;
}

/**
 * Obvious garbage (the only phone input that is refused): fewer than 5 digits (incl. letters only) or one digit
 * repeated (0000000, 1111111111).
 */
export function isPhoneGarbage(input: string | null | undefined): boolean {
  const s = String(input ?? "").trim();
  if (!s) return false;
  const digits = s.replace(/\D/g, "");
  return digits.length < 5 || /^(\d)\1+$/.test(digits);
}

/**
 * Best-effort E.164 for saving (never blocks on format): the acceptable number when there is one; else typed with
 * "+"/"00" → "+" + digits; digits already starting with the country's calling code → "+" + digits; otherwise the
 * selected/shop country's code + the national digits (trunk 0 dropped), e.g. SG "0164366293" → "+65164366293" (the
 * UI warns and offers 改用 +60…). null for garbage or when nothing fits E.164 (7–15 digits, the DB check).
 */
export function phoneBestEffort(input: string | null | undefined, country: string = "MY"): string | null {
  const raw = String(input ?? "").trim();
  if (!raw || isPhoneGarbage(raw)) return null;
  const ok = normalizePhone(raw, country);
  if (ok) return ok;
  const intl = /^(\+|00)/.test(raw.replace(/[\s\-().\u00a0/]/g, ""));
  let digits = raw.replace(/\D/g, "");
  if (intl && !raw.includes("+")) digits = digits.slice(2);
  const c = (country ?? "").trim().toUpperCase(), code = COUNTRY_CALLING_CODES[c];
  let out: string;
  if (intl || !code) out = "+" + digits;
  else if (digits.startsWith(code) && !digits.startsWith("0") && digits.length >= code.length + 7) out = "+" + digits;
  else out = "+" + code + (c === "IT" ? digits : digits.replace(/^0+/, ""));
  return /^\+[1-9]\d{6,14}$/.test(out) ? out : null;
}

/** A stored value is an acceptable (possible) E.164 number (stored-phone scan, cli --set). */
export function isValidE164(e164: string | null | undefined): boolean {
  if (!e164 || !/^\+[1-9]\d{6,14}$/.test(e164)) return false;
  return possible(parse(e164));
}
/** A stored value is a strictly valid E.164 number (libphonenumber metadata). */
export function isStrictE164(e164: string | null | undefined): boolean {
  return isValidE164(e164) && !!parse(e164!)?.isValid();
}

export interface PhoneInfo {
  /** E.164 when acceptable, else null */
  e164: string | null;
  /** acceptable → can be saved (possible for the country) */
  valid: boolean;
  /** strictly valid per libphonenumber metadata */
  strict: boolean;
  /** saved, but show the soft "请确认号码是否正确" note (valid && !strict) */
  confirm: boolean;
  /** (never-block) obvious garbage — the only input that is refused */
  garbage: boolean;
  /** (never-block) what gets saved: e164 when acceptable, else the best-effort E.164 (null for garbage / empty) */
  save: string | null;
  /** (never-block) show 「号码看起来不正确，请确认」: non-empty, not garbage, not strictly valid */
  warn: boolean;
  /** the number's country (detected from "+…" / "00…", else the selected one) */
  country: string | null;
  callingCode: string | null;
  national: string;
  international: string;
  /** the user typed an international number ("+" / "00") */
  intlInput: boolean;
}

/** Everything a phone field needs: validity, the detected country and display formats. */
export function phoneInfo(input: string, country: string): PhoneInfo {
  const s = clean(input);
  const intl = s.startsWith("+");
  const r = resolve(input, country);
  const e164 = r?.p.number ?? null;
  const p = r ? r.p : parse(input, country);
  const sel = cc(country);
  const detected = intl ? (p?.country ?? detectPhoneCountry(s)) : (p?.country ?? sel ?? null);
  return {
    e164, valid: !!e164, strict: !!r?.strict, confirm: !!r && !r.strict, country: detected ?? null,
    garbage: isPhoneGarbage(input), save: e164 ?? phoneBestEffort(input, country), warn: !!s && !r?.strict && !isPhoneGarbage(input),
    callingCode: p?.countryCallingCode ? String(p.countryCallingCode) : (sel ? COUNTRY_CALLING_CODES[sel]! : null),
    national: p && e164 ? natFormat(p) : "", international: p && e164 ? intlFormat(p) : "", intlInput: intl,
  };
}

const MAIN_COUNTRY: Record<string, string> = { "1": "US", "7": "RU", "44": "GB", "61": "AU", "39": "IT", "47": "NO", "358": "FI", "262": "RE", "590": "GP", "599": "CW", "212": "MA", "64": "NZ", "290": "SH" };
/** Country of an international entry ("+60 12…", "0060…"), or null while it can't be told yet. */
export function detectPhoneCountry(input: string): string | null {
  const s = clean(input);
  if (!s.startsWith("+")) return null;
  const p = parse(s);
  if (p?.country) return p.country;
  // the calling code alone ("+1…" / "+44…" before the number is complete): its main country
  const digits = s.slice(1);
  for (const n of [1, 2, 3]) {
    const code = digits.slice(0, n);
    if (code.length < n) break;
    if (MAIN_COUNTRY[code]) return MAIN_COUNTRY[code]!;
    const hit = getCountries().find((c) => String(getCountryCallingCode(c)) === code);
    if (hit) return hit;
  }
  return null;
}

/** Display: international ("+60 16-436 6293"), or the national format for numbers of `homeCountry`. */
export function formatPhone(e164: string | null | undefined, homeCountry?: string | null): string {
  if (!e164) return "";
  if (e164.includes("*") || e164.includes("•")) return e164; // masked
  const p = parse(e164);
  if (!possible(p)) return e164;
  return homeCountry && p.country === cc(homeCountry) ? natFormat(p) : intlFormat(p);
}

/** Countries a shop's customers most often come from (neighbours first), for the "did you mean" hint. */
const NEIGHBOURS: Record<string, string[]> = {
  SG: ["MY", "ID", "CN", "HK", "PH", "IN", "TH", "VN"], MY: ["SG", "ID", "TH", "BN", "CN", "HK", "PH"], ID: ["MY", "SG", "AU"],
  TH: ["MY", "SG", "LA", "KH", "MM"], BN: ["MY", "SG"], PH: ["SG", "MY", "HK"], VN: ["KH", "LA", "SG"], HK: ["CN", "MO", "TW", "SG", "MY"],
  CN: ["HK", "MO", "TW", "SG", "MY"], TW: ["CN", "HK"], AU: ["NZ", "GB", "SG", "MY"], NZ: ["AU", "GB"], GB: ["IE", "FR", "DE", "ES"],
  IE: ["GB"], US: ["CA", "MX"], CA: ["US"], AE: ["SA", "IN", "PK", "PH"], IN: ["AE", "SG", "MY"],
};
const COMMON = ["MY", "SG", "ID", "TH", "PH", "VN", "BN", "CN", "HK", "TW", "IN", "AU", "NZ", "GB", "US", "JP", "KR", "AE"];

export interface PhoneHint { country: string; callingCode: string; e164: string; international: string; national: string; /** "+60 16-436 6293" */ display: string }

/**
 * The number is not strictly valid for the selected country but is for a likely other one: e.g. 0164366293 with SG
 * (+65) → MY +60 16-436 6293. null when the input is strictly valid, international (+…), or matches no likely country.
 * (A possible-but-not-strict number is still accepted; the hint is only a suggestion then.)
 */
export function phoneHint(input: string, selected: string): PhoneHint | null {
  const s = clean(input);
  if (!s || s.startsWith("+") || !/^\d{6,}$/.test(s) || normalizePhoneStrict(input, selected)) return null;
  const acceptable = !!normalizePhone(input, selected);
  // accepted here (possible, only not in the metadata): suggest only a MOBILE of a neighbour country, and never for a
  // number that follows Malaysia's explicit mobile rule (01x… is Malaysian, e.g. 014-8230 0683 must not hint India)
  if (acceptable && (selected ?? "").toUpperCase() === "MY" && /^0?1/.test(s)) return null;
  const sel = (selected ?? "").toUpperCase();
  const order = [...(NEIGHBOURS[sel] ?? []), ...COMMON].filter((c, i, a) => c !== sel && a.indexOf(c) === i && supported.has(c));
  // typed with the calling code but without "+": "60164366293"
  if (!s.startsWith("0") && !acceptable) {
    const asIntl = parse("+" + s);
    if (asIntl?.isValid() && asIntl.country && asIntl.country !== sel) return hintOf(asIntl);
  }
  for (const c of order) {
    if (acceptable && !(NEIGHBOURS[sel] ?? []).includes(c)) continue;
    const p = parse(s, c);
    if (p?.isValid() && p.country === c && (!acceptable || p.getType() === "MOBILE")) return hintOf(p);
  }
  return null;
}
const hintOf = (p: PhoneNumber): PhoneHint => ({ country: p.country!, callingCode: String(p.countryCallingCode), e164: p.number, international: p.formatInternational(), national: p.formatNational(), display: displayIntl(p) });
/** "+60 16-436 6293": the calling code + the national grouping without the trunk 0 (falls back to the international format). */
function displayIntl(p: PhoneNumber): string {
  const nat = natFormat(p);
  if (nat.replace(/\D/g, "") === "0" + p.nationalNumber && nat.startsWith("0")) return "+" + p.countryCallingCode + " " + nat.slice(1).replace(/^[\s-]+/, "");
  if (nat.replace(/\D/g, "") === String(p.nationalNumber)) return "+" + p.countryCallingCode + " " + nat;
  return p.formatInternational();
}
/** Display of a stored E.164 number: "+60 16-436 6293" (national grouping), or the raw value when it can't be parsed. */
export function displayPhone(e164: string | null | undefined): string {
  if (!e164) return "";
  if (e164.includes("*") || e164.includes("•")) return e164;
  const p = parse(e164);
  return possible(p) ? displayIntl(p) : e164;
}

/** Example number for a placeholder ("9123 4567" for SG); "" when unknown. */
export function phonePlaceholder(country: string): string {
  const ex: Record<string, string> = { MY: "012-345 6789", SG: "9123 4567", AU: "0412 345 678", GB: "07400 123456", US: "(201) 555-0123", CA: "(506) 234-5678", HK: "5123 4567", ID: "0812-345-678", TH: "081 234 5678", PH: "0917 123 4567", NZ: "021 123 4567", CN: "131 2345 6789", TW: "0912 345 678", IN: "081234 56789" };
  return ex[(country ?? "").toUpperCase()] ?? "";
}

/**
 * Every country for the country-code picker: the shop's own country FIRST, then the rest A–Z by code (the web app
 * re-sorts the rest by localized name). e.g. SG shop → [SG (+65), AD, AE, …].
 */
export function phoneCountryCodes(first: string | null | undefined): Array<{ code: string; callingCode: string }> {
  const all = Object.keys(COUNTRY_CALLING_CODES).filter((c) => /^[A-Z]{2}$/.test(c) && /^\d+$/.test(COUNTRY_CALLING_CODES[c] ?? "")).sort();
  const f = (first ?? "").toUpperCase();
  const list = all.map((code) => ({ code, callingCode: COUNTRY_CALLING_CODES[code]! }));
  const i = list.findIndex((c) => c.code === f);
  if (i > 0) list.unshift(...list.splice(i, 1));
  return list;
}

/** Flag emoji from the ISO code (regional indicators). */
export function flagEmoji(code: string): string {
  const c = (code ?? "").toUpperCase();
  if (!/^[A-Z]{2}$/.test(c)) return "";
  return String.fromCodePoint(...[...c].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
}

/** Masked display for roles that only get "basic" customer access, e.g. "+60 12-***-8821". */
export function maskPhone(e164: string): string {
  if (e164.length < 8) return "***";
  return e164.slice(0, 5) + "***" + e164.slice(-4);
}
